import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { attendanceAPI, absenceAPI } from '../services/api';
import {
  Absence,
  AbsenceType,
  AnnualLeaveHistory,
  AttendanceRecord,
  PaidUnpaidLeaveHistory,
  SickLeaveHistory,
  Worker
} from '../types';
import { showCustomAlert } from './CustomAlert';

function formatDateSerbian(dateInput?: string | Date | null, withDot = true): string {
  if (!dateInput) return '—';
  const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return withDot ? `${dd}. ${mm}. ${yyyy}.` : `${dd}.${mm}.${yyyy}`;
}

function isoDay(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString().split('T')[0];
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function calcPaidUnpaidEndDate(start: Date, numberOfDays: number, excludeWeekends: boolean): Date {
  const end = new Date(start);
  end.setHours(0, 0, 0, 0);
  let countedDays = 0;

  if (excludeWeekends) {
    while (countedDays < numberOfDays) {
      const dayOfWeek = end.getDay(); // 0=Sun, 6=Sat
      if (dayOfWeek !== 0 && dayOfWeek !== 6) countedDays++;
      if (countedDays < numberOfDays) end.setDate(end.getDate() + 1);
    }
  } else {
    end.setDate(end.getDate() + numberOfDays - 1);
  }

  return end;
}

function countWeekdaysInclusive(start: Date, end: Date): number {
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  let count = 0;
  const d = new Date(s);
  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

type HeatmapKind =
  | 'sick'
  | 'annual'
  | 'paid'
  | 'unpaid'
  | 'holiday'
  | 'slava'
  | 'overtime'
  | 'h8'
  | 'h6'
  | 'h4'
  | 'none';

const monthLabels = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];

const WorkerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [worker, setWorker] = useState<Worker | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  // Year-scoped for heatmap
  const [sickYear, setSickYear] = useState<SickLeaveHistory[]>([]);
  const [annualYear, setAnnualYear] = useState<AnnualLeaveHistory[]>([]);
  const [paidUnpaidYear, setPaidUnpaidYear] = useState<PaidUnpaidLeaveHistory[]>([]);

  // All-time (tables)
  const [sickAll, setSickAll] = useState<SickLeaveHistory[]>([]);
  const [annualAll, setAnnualAll] = useState<AnnualLeaveHistory[]>([]);

  const [holidays, setHolidays] = useState<Absence[]>([]);
  const [absenceTypes, setAbsenceTypes] = useState<AbsenceType[]>([]);

  // Modals
  const [sickModal, setSickModal] = useState(false);
  const [annualModal, setAnnualModal] = useState(false);
  const [leaveModal, setLeaveModal] = useState(false);
  const [modalError, setModalError] = useState('');

  const [sickForm, setSickForm] = useState({
    startDate: new Date(),
    diagnosis: '',
    excludeWeekends: false,
    notes: ''
  });

  const [annualForm, setAnnualForm] = useState({
    leaveType: 'new' as 'new' | 'old',
    startDate: new Date(),
    endOrReturnDate: new Date(), // UI field
    excludeWeekends: false,
    notes: ''
  });

  const [leaveForm, setLeaveForm] = useState({
    absenceId: '' as string,
    startDate: new Date(),
    numberOfDays: 1,
    notes: ''
  });

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const base = [];
    for (let y = now; y >= now - 5; y--) base.push({ label: String(y), value: y });
    return base;
  }, []);

  const positionName = useMemo(() => {
    if (!worker) return '—';
    if (!worker.positionId) return '—';
    if (typeof worker.positionId === 'string') return '—';
    return worker.positionId.name;
  }, [worker]);

  const slavaDisplay = useMemo(() => {
    if (!worker?.slavaName) return '—';
    return worker.slavaName;
  }, [worker]);

  const slavaDateDisplay = useMemo(() => {
    if (worker?.slavaMonth === undefined || worker?.slavaDay === undefined || worker?.slavaMonth === null || worker?.slavaDay === null) return '—';
    const dd = String(worker.slavaDay).padStart(2, '0');
    const mm = String(worker.slavaMonth + 1).padStart(2, '0');
    return `${dd}.${mm}.`;
  }, [worker]);

  const totalLeaveLeft = useMemo(() => {
    if (!worker) return 0;
    return (worker.annualLeaveDaysOld || 0) + (worker.annualLeaveDaysNew || 0);
  }, [worker]);

  const fetchAll = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const w = await attendanceAPI.getWorkerById(id);
      setWorker(w);
      if (w.annualLeaveYear) setSelectedYear(w.annualLeaveYear);
    } catch (e: any) {
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju radnika');
    } finally {
      setLoading(false);
    }
  };

  const fetchYearData = async (year: number) => {
    if (!id) return;
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31, 23, 59, 59, 999);
    const [rec, sickY, annualY, paidY] = await Promise.all([
      attendanceAPI.getAttendanceRecordsForWorker({ workerId: id, startDate: start.toISOString(), endDate: end.toISOString() }),
      attendanceAPI.getSickLeaveHistoryYear(id, year),
      attendanceAPI.getAnnualLeaveHistoryYear(id, year),
      attendanceAPI.getPaidUnpaidLeaveHistoryYear(id, year)
    ]);
    setRecords(rec);
    setSickYear(sickY);
    setAnnualYear(annualY);
    setPaidUnpaidYear(paidY);
  };

  const fetchHistories = async () => {
    if (!id) return;
    const [sickAllData, annualAllData] = await Promise.all([
      attendanceAPI.getSickLeaveHistory(id),
      attendanceAPI.getAnnualLeaveHistory(id)
    ]);
    setSickAll(sickAllData);
    setAnnualAll(annualAllData);
  };

  const fetchAbsenceDefinitions = async () => {
    try {
      const [allAbsences, types] = await Promise.all([
        absenceAPI.getAll({ isActive: true }),
        attendanceAPI.getAbsenceTypes({ isActive: true })
      ]);
      setHolidays(allAbsences.filter((a) => a.category === 'holiday'));
      setAbsenceTypes(types);
    } catch (e) {
      console.error('Error fetching absences:', e);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchAbsenceDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchYearData(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedYear]);

  useEffect(() => {
    fetchHistories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const annualComputedDays = useMemo(() => {
    const s = annualForm.startDate;
    const e = annualForm.endOrReturnDate;
    if (!s || !e) return 1;
    const start = new Date(s);
    const end = new Date(e);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    if (!annualForm.excludeWeekends) {
      const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(1, diff + 1);
    }
    // Treat end as RETURN date (not counted). Count weekdays from start..(end-1)
    const endMinusOne = addDays(end, -1);
    return Math.max(1, countWeekdaysInclusive(start, endMinusOne));
  }, [annualForm]);

  const leaveComputedEndDate = useMemo(() => {
    // Production UI for "Dodaj odsustvo" is calendar-days based (no weekend toggle)
    return calcPaidUnpaidEndDate(leaveForm.startDate, leaveForm.numberOfDays, false);
  }, [leaveForm.startDate, leaveForm.numberOfDays]);

  const openSick = () => {
    setModalError('');
    setSickForm({ startDate: new Date(), diagnosis: '', excludeWeekends: false, notes: '' });
    setSickModal(true);
  };

  const openAnnual = () => {
    setModalError('');
    setAnnualForm({ leaveType: 'new', startDate: new Date(), endOrReturnDate: new Date(), excludeWeekends: false, notes: '' });
    setAnnualModal(true);
  };

  const openLeave = () => {
    setModalError('');
    setLeaveForm({ absenceId: '', startDate: new Date(), numberOfDays: 1, notes: '' });
    setLeaveModal(true);
  };

  // When selecting an absence type, prefill numberOfDays from defaultDays (editable in WorkerDetails UI)
  useEffect(() => {
    if (!leaveForm.absenceId) return;
    const t = absenceTypes.find((x) => x._id === leaveForm.absenceId);
    if (!t) return;
    setLeaveForm((prev) => ({ ...prev, numberOfDays: t.defaultDays || 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveForm.absenceId, absenceTypes.length]);

  const submitSick = async () => {
    if (!id) return;
    try {
      setModalError('');
      await attendanceAPI.startSickLeave({
        workerId: id,
        startDate: sickForm.startDate.toISOString(),
        diagnosis: sickForm.diagnosis || undefined,
        excludeWeekends: sickForm.excludeWeekends,
        notes: sickForm.notes || undefined
      });
      showCustomAlert.success('Bolovanje je sačuvano');
      setSickModal(false);
      await fetchAll();
      await fetchYearData(selectedYear);
      await fetchHistories();
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Greška pri čuvanju bolovanja');
    }
  };

  const submitAnnual = async () => {
    if (!id || !worker) return;
    try {
      setModalError('');
      await attendanceAPI.createAnnualLeave({
        workerId: id,
        startDate: annualForm.startDate.toISOString(),
        numberOfDays: annualComputedDays,
        leaveType: annualForm.leaveType,
        excludeWeekends: annualForm.excludeWeekends,
        notes: annualForm.notes || undefined
      });
      showCustomAlert.success('Godišnji odmor je sačuvan');
      setAnnualModal(false);
      await fetchAll();
      await fetchYearData(selectedYear);
      await fetchHistories();
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Greška pri čuvanju godišnjeg odmora');
    }
  };

  const submitLeave = async () => {
    if (!id) return;
    try {
      setModalError('');
      const def = absenceTypes.find((a) => a._id === leaveForm.absenceId);
      if (!def) {
        setModalError('Izaberite odsustvo.');
        return;
      }
      await attendanceAPI.createPaidUnpaidLeave({
        workerId: id,
        startDate: leaveForm.startDate.toISOString(),
        leaveType: def.type,
        numberOfDays: leaveForm.numberOfDays,
        reason: def.name,
        excludeWeekends: false,
        notes: leaveForm.notes || undefined
      });
      showCustomAlert.success('Odsustvo je sačuvano');
      setLeaveModal(false);
      await fetchAll(); // refresh annual leave counters (odsustva oduzimaju dane)
      await fetchYearData(selectedYear);
      await fetchHistories();
    } catch (e: any) {
      setModalError(e?.response?.data?.message || 'Greška pri čuvanju odsustva');
    }
  };

  // ---- Heatmap ----
  const heatmap = useMemo(() => {
    const map = new Map<string, { kind: HeatmapKind; label: string }>();
    const prio: Record<HeatmapKind, number> = {
      sick: 100,
      annual: 90,
      slava: 80,
      holiday: 70,
      paid: 60,
      unpaid: 60,
      overtime: 50,
      h8: 40,
      h6: 35,
      h4: 30,
      none: 0
    };
    const setIfHigher = (key: string, next: { kind: HeatmapKind; label: string }) => {
      const prev = map.get(key);
      if (!prev || prio[next.kind] > prio[prev.kind]) map.set(key, next);
    };

    // From records
    records.forEach((r) => {
      const d = new Date(r.date);
      const key = isoDay(d);
      if (r.isSickLeave) setIfHigher(key, { kind: 'sick', label: 'Bolovanje' });
      else if (r.isAnnualLeave) setIfHigher(key, { kind: 'annual', label: 'Godišnji odmor' });
      else if (r.isSlava) setIfHigher(key, { kind: 'slava', label: 'Slava' });
      else if (r.overtimeHours > 0) setIfHigher(key, { kind: 'overtime', label: `${r.overtimeHours}h prekovremeno` });
      else if (r.hoursWorked >= 8) setIfHigher(key, { kind: 'h8', label: `${r.hoursWorked}h rada` });
      else if (r.hoursWorked >= 6) setIfHigher(key, { kind: 'h6', label: `${r.hoursWorked}h rada` });
      else if (r.hoursWorked >= 4) setIfHigher(key, { kind: 'h4', label: `${r.hoursWorked}h rada` });
      else if (r.hoursWorked > 0) setIfHigher(key, { kind: 'h4', label: `${r.hoursWorked}h rada` });
      else setIfHigher(key, { kind: 'none', label: 'Nije radio' });
    });

    // From sick history (fill missing days)
    sickYear.forEach((s) => {
      const start = new Date(s.startDate);
      const end = s.endDate ? new Date(s.endDate) : new Date(selectedYear, 11, 31);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const d = new Date(start);
      while (d <= end) {
        const day = d.getDay();
        if (s.excludeWeekends && (day === 0 || day === 6)) {
          d.setDate(d.getDate() + 1);
          continue;
        }
        const key = isoDay(d);
        setIfHigher(key, { kind: 'sick', label: 'Bolovanje' });
        d.setDate(d.getDate() + 1);
      }
    });

    // From annual history
    annualYear.forEach((a) => {
      const start = new Date(a.startDate);
      const end = new Date(a.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const d = new Date(start);
      while (d <= end) {
        const key = isoDay(d);
        setIfHigher(key, { kind: 'annual', label: 'Godišnji odmor' });
        d.setDate(d.getDate() + 1);
      }
    });

    // Paid/unpaid leave history (year-specific)
    paidUnpaidYear.forEach((p) => {
      const start = new Date(p.startDate);
      const end = new Date(p.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const d = new Date(start);
      while (d <= end) {
        const day = d.getDay();
        if (p.excludeWeekends && (day === 0 || day === 6)) {
          d.setDate(d.getDate() + 1);
          continue;
        }
        const key = isoDay(d);
        setIfHigher(key, {
          kind: p.leaveType === 'paid' ? 'paid' : 'unpaid',
          label: p.leaveType === 'paid' ? 'Plaćeno odsustvo' : 'Neplaćeno odsustvo'
        });
        d.setDate(d.getDate() + 1);
      }
    });

    // Worker slava (recurring, one day)
    if (worker?.slavaMonth !== undefined && worker?.slavaDay !== undefined && worker?.slavaMonth !== null && worker?.slavaDay !== null) {
      const sd = new Date(selectedYear, worker.slavaMonth, worker.slavaDay);
      if (!Number.isNaN(sd.getTime())) {
        const key = isoDay(sd);
        setIfHigher(key, { kind: 'slava', label: `${worker.slavaName || 'Slava'} ${String(worker.slavaDay).padStart(2, '0')}.${String(worker.slavaMonth + 1).padStart(2, '0')} (8h)` });
      }
    }

    // Holidays (recurring)
    holidays.forEach((h) => {
      if (!h.isActive) return;
      // recurring holiday
      if (h.isRecurring && h.recurringMonth !== undefined && h.recurringDay !== undefined) {
        const start = new Date(selectedYear, h.recurringMonth, h.recurringDay);
        let end = start;
        if (h.recurringEndMonth !== undefined && h.recurringEndDay !== undefined) {
          end = new Date(selectedYear, h.recurringEndMonth, h.recurringEndDay);
        }
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const d = new Date(start);
        while (d <= end) {
              setIfHigher(isoDay(d), { kind: 'holiday', label: `${h.name} (8h)` });
          d.setDate(d.getDate() + 1);
        }
      } else {
        // non-recurring range
        const start = new Date(h.startDate);
        const end = new Date(h.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const d = new Date(start);
        while (d <= end) {
              if (d.getFullYear() === selectedYear) setIfHigher(isoDay(d), { kind: 'holiday', label: `${h.name} (8h)` });
          d.setDate(d.getDate() + 1);
        }
      }
    });

    return map;
  }, [records, sickYear, annualYear, paidUnpaidYear, selectedYear, holidays, worker]);

  const kindToColor: Record<HeatmapKind, string> = {
    sick: '#ef4444', // red
    annual: '#38bdf8', // cyan/blue
    slava: '#ec4899', // pink
    holiday: '#f59e0b', // amber
    paid: '#7c3aed', // violet
    unpaid: '#fb923c', // orange
    overtime: '#166534', // dark green
    h8: '#22c55e',
    h6: '#4ade80',
    h4: '#86efac',
    none: '#e5e7eb'
  };

  const renderMonth = (year: number, month: number) => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = first.getDay(); // 0 Sun

    const cells: Array<{ day?: number; key?: string }> = [];
    for (let i = 0; i < startWeekday; i++) cells.push({});
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day);
      cells.push({ day, key: isoDay(d) });
    }
    while (cells.length % 7 !== 0) cells.push({});

    return (
      <div style={{ width: 220 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{monthLabels[month]}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {cells.map((c, idx) => {
            if (!c.day || !c.key) return <div key={`e-${idx}`} style={{ height: 22 }} />;
            const info = heatmap.get(c.key);
            const color = kindToColor[info?.kind || 'none'];
            const tooltip = info ? `${formatDateSerbian(new Date(c.key))} - ${info.label}` : `${formatDateSerbian(new Date(c.key))}`;
            return (
              <div
                key={c.key}
                title={tooltip}
                style={{
                  height: 22,
                  borderRadius: 4,
                  background: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: info?.kind === 'none' ? '#6b7280' : '#0b1220'
                }}
              >
                {c.day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!id) return null;

  return (
    <div className="p-4">
      <div className="mb-3">
        <Button label="Nazad" icon="pi pi-arrow-left" className="p-button-outlined" onClick={() => navigate('/workers')} />
      </div>

      <Card>
        <div className="flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h2 className="m-0">{worker ? `${worker.lastName} ${worker.firstName}` : 'Učitavanje...'}</h2>
            <div className="mt-3" style={{ lineHeight: 1.9 }}>
              <div><b>ID radnika:</b> {worker?.employeeId || 'N/A'}</div>
              <div><b>Pozicija:</b> {positionName}</div>
              <div><b>Datum zaposlenja:</b> {formatDateSerbian(worker?.dateOfEmployment || null)}</div>
              <div><b>Status:</b> {worker?.isActive ? 'Aktivan' : 'Neaktivan'}</div>
              <div><b>Slava:</b> {slavaDisplay}</div>
              <div><b>Datum slave:</b> {slavaDateDisplay}</div>
            </div>
          </div>

          <div style={{ minWidth: 320 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{`Godišnji odmor (${worker?.annualLeaveYear || selectedYear}):`}</div>
            <div style={{ lineHeight: 1.9 }}>
              <div><b>Stari dani:</b> {worker?.annualLeaveDaysOld ?? 0} <span className="text-600">(koristi se do 30. juna)</span></div>
              <div><b>Novi dani:</b> {worker?.annualLeaveDaysNew ?? 0}</div>
              <div><b>Ukupno preostalo:</b> {totalLeaveLeft} dana</div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button label="Pošalji na bolovanje" icon="pi pi-times" severity="danger" onClick={openSick} />
          <Button label="Dodaj godišnji odmor" icon="pi pi-calendar" severity="info" onClick={openAnnual} />
          <Button label="Dodaj odsustvo" icon="pi pi-clock" severity="warning" onClick={openLeave} />
        </div>
      </Card>

      <div className="mt-4">
        <Card title="Istorija bolovanja">
          <DataTable value={sickAll} emptyMessage="Nema evidencije bolovanja" className="p-datatable-sm">
            <Column header="Datum" body={(r: SickLeaveHistory) => formatDateSerbian(r.startDate)} />
            <Column header="Dijagnoza" field="diagnosis" />
            <Column header="Vikend" body={(r: SickLeaveHistory) => (r.excludeWeekends ? 'Ne računaj vikend' : '')} />
            <Column header="Napomene" field="notes" />
            <Column header="Status" body={(r: SickLeaveHistory) => (r.isActive ? 'Aktivno' : '')} />
          </DataTable>
        </Card>
      </div>

      <div className="mt-4">
        <Card title="Istorija godišnjih odmora">
          <DataTable value={annualAll} emptyMessage="Nema evidencije godišnjih odmora" className="p-datatable-sm">
            <Column header="Period" body={(r: AnnualLeaveHistory) => `${formatDateSerbian(r.startDate)} - ${formatDateSerbian(r.endDate)}`} />
            <Column header="Broj dana" field="numberOfDays" />
            <Column header="Tip" body={(r: AnnualLeaveHistory) => (r.leaveType === 'new' ? 'Novi' : 'Stari')} />
            <Column header="Vikend" body={(r: AnnualLeaveHistory) => (r.excludeWeekends ? 'Ne računaj vikend' : '')} />
            <Column header="Napomene" field="notes" />
          </DataTable>
        </Card>
      </div>

      <div className="mt-4">
        <Card title="Istorija odsustava">
          <DataTable value={paidUnpaidYear} emptyMessage="Nema evidencije odsustava" className="p-datatable-sm">
            <Column header="Period" body={(r: PaidUnpaidLeaveHistory) => `${formatDateSerbian(r.startDate)} - ${formatDateSerbian(r.endDate)}`} />
            <Column header="Naziv" field="reason" />
            <Column header="Tip" body={(r: PaidUnpaidLeaveHistory) => (r.leaveType === 'paid' ? 'Plaćeno' : 'Neplaćeno')} />
            <Column header="Opis" field="notes" />
          </DataTable>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <div className="flex justify-content-between align-items-center mb-3">
            <h3 className="m-0">{`Heatmap prisustva - ${selectedYear}`}</h3>
            <Dropdown value={selectedYear} options={yearOptions} onChange={(e) => setSelectedYear(e.value)} appendTo="self" />
          </div>

          {/* 6 months per row (2 rows). On smaller screens this container scrolls horizontally. */}
          <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 220px)',
                gap: 18,
                minWidth: 1410 // 6 * 220 + 5 * 18
              }}
            >
              {Array.from({ length: 12 }).map((_, m) => (
                <div key={m}>{renderMonth(selectedYear, m)}</div>
              ))}
            </div>
          </div>

          <div className="mt-4 p-3 border-1 border-round surface-border">
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Legenda:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.sick, display: 'inline-block', borderRadius: 4 }} /> Bolovanje</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.annual, display: 'inline-block', borderRadius: 4 }} /> Godišnji odmor</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.slava, display: 'inline-block', borderRadius: 4 }} /> Slava</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.holiday, display: 'inline-block', borderRadius: 4 }} /> Državni praznici</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.paid, display: 'inline-block', borderRadius: 4 }} /> Plaćeno odsustvo</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.unpaid, display: 'inline-block', borderRadius: 4 }} /> Neplaćeno odsustvo</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.overtime, display: 'inline-block', borderRadius: 4 }} /> Prekovremeni</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.h8, display: 'inline-block', borderRadius: 4 }} /> 8h rada</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.h6, display: 'inline-block', borderRadius: 4 }} /> 6h rada</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.h4, display: 'inline-block', borderRadius: 4 }} /> 4h rada</div>
              <div className="flex align-items-center gap-2"><span style={{ width: 36, height: 10, background: kindToColor.none, display: 'inline-block', borderRadius: 4, border: '1px solid #d1d5db' }} /> Nije radio</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Sick Leave Modal */}
      <Dialog header="Pošalji na bolovanje" visible={sickModal} onHide={() => setSickModal(false)} style={{ width: '520px' }} className="p-fluid" draggable={false}>
        {modalError && <Message severity="error" text={modalError} className="mb-3" />}
        <div className="field">
          <label className="block mb-2">
            Datum početka bolovanja <span className="text-red-500">*</span>
          </label>
          <Calendar value={sickForm.startDate} onChange={(e) => setSickForm({ ...sickForm, startDate: e.value as Date })} dateFormat="dd.mm.yy" showIcon />
        </div>
        <div className="field">
          <label className="block mb-2">Dijagnoza</label>
          <InputTextarea value={sickForm.diagnosis} onChange={(e) => setSickForm({ ...sickForm, diagnosis: e.target.value })} rows={3} />
        </div>
        <div className="field flex align-items-center gap-2">
          <Checkbox inputId="sick-ex-wknd" checked={sickForm.excludeWeekends} onChange={(e) => setSickForm({ ...sickForm, excludeWeekends: !!e.checked })} />
          <label htmlFor="sick-ex-wknd">Ne računaj vikend</label>
        </div>
        <div className="field">
          <label className="block mb-2">Napomene</label>
          <InputTextarea value={sickForm.notes} onChange={(e) => setSickForm({ ...sickForm, notes: e.target.value })} rows={3} />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setSickModal(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" onClick={submitSick} />
        </div>
      </Dialog>

      {/* Annual Leave Modal */}
      <Dialog header="Dodaj godišnji odmor" visible={annualModal} onHide={() => setAnnualModal(false)} style={{ width: '520px' }} className="p-fluid" draggable={false}>
        {modalError && <Message severity="error" text={modalError} className="mb-3" />}
        <div className="field">
          <label className="block mb-2">
            Tip godišnjeg <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={annualForm.leaveType}
            options={[
              { label: `Novi (${worker?.annualLeaveDaysNew ?? 0} dana preostalo)`, value: 'new' },
              { label: `Stari (${worker?.annualLeaveDaysOld ?? 0} dana preostalo)`, value: 'old' }
            ]}
            onChange={(e) => setAnnualForm({ ...annualForm, leaveType: e.value })}
            appendTo="self"
          />
        </div>
        <div className="field">
          <label className="block mb-2">
            Datum odlaska <span className="text-red-500">*</span>
          </label>
          <Calendar value={annualForm.startDate} onChange={(e) => setAnnualForm({ ...annualForm, startDate: e.value as Date })} dateFormat="dd.mm.yy" showIcon />
        </div>
        <div className="field">
          <label className="block mb-2">
            Datum povratka <span className="text-red-500">*</span>
          </label>
          <Calendar value={annualForm.endOrReturnDate} onChange={(e) => setAnnualForm({ ...annualForm, endOrReturnDate: e.value as Date })} dateFormat="dd.mm.yy" showIcon />
        </div>
        <div className="field flex align-items-center gap-2">
          <Checkbox inputId="annual-ex-wknd" checked={annualForm.excludeWeekends} onChange={(e) => setAnnualForm({ ...annualForm, excludeWeekends: !!e.checked })} />
          <label htmlFor="annual-ex-wknd">Ne računaj vikend</label>
        </div>
        <div className="field">
          <label className="block mb-2">Broj dana (automatski)</label>
          <InputText value={`${annualComputedDays} dan${annualComputedDays === 1 ? '' : 'a'}`} disabled />
        </div>
        <div className="field">
          <label className="block mb-2">Napomene</label>
          <InputTextarea value={annualForm.notes} onChange={(e) => setAnnualForm({ ...annualForm, notes: e.target.value })} rows={3} />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setAnnualModal(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" onClick={submitAnnual} />
        </div>
      </Dialog>

      {/* Paid/Unpaid Leave Modal */}
      <Dialog header="Dodaj odsustvo" visible={leaveModal} onHide={() => setLeaveModal(false)} style={{ width: '520px' }} className="p-fluid" draggable={false}>
        {modalError && <Message severity="error" text={modalError} className="mb-3" />}
        <div className="field">
          <label className="block mb-2">
            Izaberite odsustvo <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={leaveForm.absenceId}
            options={absenceTypes.map((a) => ({ label: a.name, value: a._id }))}
            placeholder="Izaberite odsustvo"
            onChange={(e) => setLeaveForm({ ...leaveForm, absenceId: e.value })}
            appendTo="self"
          />
        </div>
        <div className="field">
          <label className="block mb-2">
            Datum početka <span className="text-red-500">*</span>
          </label>
          <Calendar value={leaveForm.startDate} onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.value as Date })} dateFormat="dd.mm.yy" showIcon />
        </div>
        <div className="field">
          <label className="block mb-2">
            Broj dana <span className="text-red-500">*</span>
          </label>
          <InputNumber value={leaveForm.numberOfDays} onValueChange={(e) => setLeaveForm({ ...leaveForm, numberOfDays: e.value ?? 1 })} min={1} />
        </div>
        <div className="field">
          <label className="block mb-2">Datum završetka (automatski)</label>
          <InputText value={formatDateSerbian(leaveComputedEndDate)} disabled />
        </div>
        <div className="field">
          <label className="block mb-2">Opis (opciono)</label>
          <InputTextarea value={leaveForm.notes} onChange={(e) => setLeaveForm({ ...leaveForm, notes: e.target.value })} rows={3} placeholder="Dodatni opis..." />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setLeaveModal(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" onClick={submitLeave} />
        </div>
      </Dialog>

      {loading && <div className="mt-3 text-600">Učitavanje...</div>}
    </div>
  );
};

export default WorkerDetails;


